import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { AIContentClassification, SupportedAIApp } from '../types';
import { getDisclosureCopy } from '../disclosure/copy';
import { getAITransparencyFlags } from '../config/flags';

const TOOLTIP_GAP = 8;
const VIEWPORT_PADDING = 16;

type TooltipCoords = {
  top: number;
  left: number;
};

function measureTooltipCoords(anchor: HTMLElement, tooltip: HTMLElement): TooltipCoords {
  const rect = anchor.getBoundingClientRect();
  const width = tooltip.offsetWidth || 256;
  const height = tooltip.offsetHeight || 88;
  const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING;
  const spaceAbove = rect.top - VIEWPORT_PADDING;
  const placeBelow = spaceBelow >= height + TOOLTIP_GAP || spaceBelow >= spaceAbove;

  let top = placeBelow ? rect.bottom + TOOLTIP_GAP : rect.top - height - TOOLTIP_GAP;
  // Prefer lining up with the badge; shift left if it would overflow the viewport.
  let left = rect.left;
  if (left + width > window.innerWidth - VIEWPORT_PADDING) {
    left = rect.right - width;
  }
  if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;
  if (left + width > window.innerWidth - VIEWPORT_PADDING) {
    left = Math.max(VIEWPORT_PADDING, window.innerWidth - VIEWPORT_PADDING - width);
  }
  if (top < VIEWPORT_PADDING) top = VIEWPORT_PADDING;
  if (top + height > window.innerHeight - VIEWPORT_PADDING) {
    top = Math.max(VIEWPORT_PADDING, window.innerHeight - VIEWPORT_PADDING - height);
  }

  return { top, left };
}

export type AIContentDisclosureProps = {
  classification: AIContentClassification;
  operation?: string;
  app?: SupportedAIApp;
  /** Force show even if feature flag is off (e.g. tests) */
  force?: boolean;
  /** Visual size */
  size?: 'sm' | 'md';
  className?: string;
  /** When true, render a compact inline badge only (no expandable panel by default) */
  compact?: boolean;
};

/**
 * Subtle, accessible AI transparency badge.
 * Hover (desktop) / tap (mobile) reveals the full explanation.
 * Not a warning banner — informative for property marketing use.
 */
export function AIContentDisclosure({
  classification,
  operation,
  app,
  force = false,
  size = 'sm',
  className = '',
  compact = false,
}: AIContentDisclosureProps) {
  const flags = getAITransparencyFlags();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<TooltipCoords | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const hoverCloseTimer = useRef<number | null>(null);
  const tooltipId = useId();
  const copy = getDisclosureCopy({ classification, operation, app });

  const close = useCallback(() => setOpen(false), []);

  const cancelHoverClose = useCallback(() => {
    if (hoverCloseTimer.current != null) {
      window.clearTimeout(hoverCloseTimer.current);
      hoverCloseTimer.current = null;
    }
  }, []);

  const scheduleHoverClose = useCallback(() => {
    if (!window.matchMedia('(hover: hover)').matches) return;
    cancelHoverClose();
    hoverCloseTimer.current = window.setTimeout(() => setOpen(false), 120);
  }, [cancelHoverClose]);

  const reposition = useCallback(() => {
    if (!rootRef.current || !tooltipRef.current) return;
    setCoords(measureTooltipCoords(rootRef.current, tooltipRef.current));
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    reposition();
    const frame = window.requestAnimationFrame(reposition);
    return () => window.cancelAnimationFrame(frame);
  }, [open, reposition, copy.label, copy.description, copy.supporting]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target) || tooltipRef.current?.contains(target)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, close, reposition]);

  useEffect(() => () => cancelHoverClose(), [cancelHoverClose]);

  if (!force && !flags.aiTransparencyEnabled) {
    return null;
  }

  // Assisted outputs: subtle by default; still available for info panels
  const isSubtle = classification === 'AI_ASSISTED';
  const pad = size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs';
  const tone = isSubtle
    ? 'bg-slate-800/70 text-slate-300 border-slate-600/60'
    : 'bg-slate-800/80 text-slate-200 border-slate-500/50';

  return (
    <div ref={rootRef} className={`relative inline-flex ${className}`}>
      <button
        type="button"
        className={`inline-flex items-center gap-1 rounded-md border ${tone} ${pad} font-medium tracking-wide focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900`}
        aria-expanded={open}
        aria-controls={tooltipId}
        aria-label={`${copy.label}. ${copy.description}`}
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => {
          if (window.matchMedia('(hover: hover)').matches) {
            cancelHoverClose();
            setOpen(true);
          }
        }}
        onMouseLeave={scheduleHoverClose}
        onFocus={() => setOpen(true)}
        onBlur={(e) => {
          const next = e.relatedTarget as Node | null;
          if (rootRef.current?.contains(next) || tooltipRef.current?.contains(next)) return;
          setOpen(false);
        }}
      >
        <span aria-hidden="true" className="text-teal-300/90">
          ✦
        </span>
        <span>{copy.label}</span>
      </button>

      {open &&
        !compact &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={tooltipRef}
            id={tooltipId}
            role="tooltip"
            onMouseEnter={cancelHoverClose}
            onMouseLeave={scheduleHoverClose}
            className="fixed z-[80] rounded-lg border border-slate-600/70 bg-slate-900/95 p-3 text-left shadow-lg backdrop-blur-sm"
            style={{
              top: coords?.top ?? 0,
              left: coords?.left ?? 0,
              width: 256,
              maxWidth: 'calc(100vw - 2rem)',
              opacity: coords ? 1 : 0,
              pointerEvents: coords ? 'auto' : 'none',
            }}
          >
            <p className="text-xs font-semibold text-slate-100">{copy.label}</p>
            <p className="mt-1 text-[11px] leading-relaxed text-slate-300">{copy.description}</p>
            {copy.supporting && (
              <p className="mt-1.5 text-[11px] leading-relaxed text-slate-400">{copy.supporting}</p>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}

export type AIInfoPanelProps = {
  classification: AIContentClassification;
  operation?: string;
  operationLabel?: string;
  createdAt?: string;
  hasSource?: boolean;
  app?: SupportedAIApp;
  className?: string;
};

/** Compact AI information block for asset details panels. */
export function AIInfoPanel({
  classification,
  operation,
  operationLabel,
  createdAt,
  hasSource = true,
  app,
  className = '',
}: AIInfoPanelProps) {
  const flags = getAITransparencyFlags();
  if (!flags.aiTransparencyEnabled) return null;

  const copy = getDisclosureCopy({ classification, operation, app });
  const created = createdAt
    ? new Date(createdAt).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  return (
    <section
      className={`rounded-lg border border-slate-700/60 bg-slate-900/40 p-4 ${className}`}
      aria-labelledby="ai-info-heading"
    >
      <h3 id="ai-info-heading" className="text-sm font-semibold text-slate-100">
        AI information
      </h3>
      <dl className="mt-3 space-y-2 text-xs text-slate-300">
        <div className="flex justify-between gap-4">
          <dt className="text-slate-500">Status</dt>
          <dd className="font-medium text-slate-200">{copy.label}</dd>
        </div>
        {(operationLabel || operation) && (
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500">Operation</dt>
            <dd className="font-medium text-slate-200">{operationLabel || operation}</dd>
          </div>
        )}
        {created && (
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500">Created</dt>
            <dd className="font-medium text-slate-200">{created}</dd>
          </div>
        )}
        {hasSource && (
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500">Source</dt>
            <dd className="font-medium text-slate-200">Original uploaded photograph</dd>
          </div>
        )}
      </dl>
      <p className="mt-3 text-[11px] leading-relaxed text-slate-400">{copy.description}</p>
    </section>
  );
}

export default AIContentDisclosure;
